// client/src/components/marketer/MarketerProjectFinishCheck.js
import React, { useState, useEffect, useCallback } from 'react';
import ProjectDetailModal from './project/modals/ProjectDetailModal';
import MarketerApplicationsViewModal from './modals/MarketerApplicationsViewModal';
import MarketerResultStatusModal from './modals/MarketerResultStatusModal';
import MarketerCPSSettlementModal from './modals/MarketerCPSSettlementModal';

const API_URL = process.env.REACT_APP_API_URL;
const LIMIT = 10;

function MarketerProjectFinishCheck() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // 모달 상태
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showApplicationsModal, setShowApplicationsModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showCPSModal, setShowCPSModal] = useState(false);

  // 검색 및 페이지네이션 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // 프로젝트 선택 및 상세 정보 조회
  const handleSelectProject = useCallback(async (project) => {
    setSelectedProject(project);
    setLoadingDetail(true);

    try {
      const res = await fetch(`${API_URL}/api/marketer/projects/${project.id}/completed-detail`, {
        credentials: 'include'
      });
      const data = await res.json();

      if (data.ok) {
        setSelectedProject({
          ...project,
          ...data.project
        });
      } else {
        console.error('상세 정보 조회 실패:', data.message);
      }
    } catch (error) {
      console.error('상세 정보 조회 오류:', error);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  // 프로젝트 목록 조회
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: LIMIT
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const res = await fetch(`${API_URL}/api/marketer/projects/completed?${params}`, {
        credentials: 'include'
      });
      const data = await res.json();

      if (data.ok) {
        setProjects(data.projects || []);
        setTotalCount(data.total || 0);
        setTotalPages(data.totalPages || 1);

        // 첫 번째 프로젝트 자동 선택 (상세 정보 포함)
        if (data.projects && data.projects.length > 0) {
          handleSelectProject(data.projects[0]);
        } else {
          setSelectedProject(null);
        }
      } else {
        alert('목록 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('완료된 프로젝트 목록 조회 오류:', error);
      alert('목록 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, handleSelectProject]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // 검색 실행
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  // 검색 초기화
  const handleResetSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  // 페이지 변경
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
  };

  // 팔로워 수 포맷팅
  const formatFollowers = (count) => {
    if (!count) return '0';
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  return (
    <div>
      {/* 상단 안내 */}
      <div className="mb-page-header">
        <h2 className="mb-page-title">완료 프로젝트 성과 보기</h2>
        <p className="mb-page-description">완료된 프로젝트의 성과와 정산 결과를 확인하세요.</p>
      </div>

      <div className="finish">
        {/* 왼쪽 목록 */}
        <div className="flist">
          <div className="flist__head">
            완료된 프로젝트 <span>총 {totalCount}개</span>
          </div>

          <div className="flist__search">
            <input
              type="text"
              placeholder="번호 또는 제목 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button
              type="button"
              onClick={handleSearch}
              className="mb-btn mb-btn-secondary mb-btn-sm"
            >
              검색
            </button>
          </div>

          {searchTerm && (
            <button
              type="button"
              onClick={handleResetSearch}
              style={{
                display: 'block',
                width: '100%',
                padding: '0 18px 12px',
                fontSize: 12.5,
                fontWeight: 600,
                color: '#2f6bff',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                textAlign: 'center'
              }}
            >
              검색 초기화
            </button>
          )}

          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#6b7686', fontSize: 13 }}>
              로딩 중...
            </div>
          ) : projects.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#6b7686', fontSize: 13 }}>
              완료된 프로젝트가 없습니다.
            </div>
          ) : (
            projects.map((project) => (
              <button
                key={project.id}
                type="button"
                onClick={() => handleSelectProject(project)}
                className={`fitem ${selectedProject?.id === project.id ? 'is-active' : ''}`}
              >
                <span className="fitem__no">NO.{project.projectNumber}</span>
                <div className="fitem__t" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {project.displayTitle || project.productName}
                </div>
                <span className="fitem__d">완료일 {formatDate(project.completedAt)}</span>
              </button>
            ))
          )}

          {totalPages > 1 && (
            <div className="pager" style={{ padding: '14px 0' }}>
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="이전 페이지"
              >
                ‹
              </button>
              {currentPage} / {totalPages}
              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                aria-label="다음 페이지"
              >
                ›
              </button>
            </div>
          )}
        </div>

        {/* 오른쪽 상세 */}
        <div>
          {!selectedProject ? (
            <div className="mb-card" style={{ textAlign: 'center', padding: 60, color: '#6b7686', fontSize: 14 }}>
              프로젝트를 선택하세요
            </div>
          ) : loadingDetail ? (
            <div className="mb-card" style={{ textAlign: 'center', padding: 60, color: '#6b7686', fontSize: 14 }}>
              로딩 중...
            </div>
          ) : (
            <>
              {/* 헤더: 프로젝트 번호 + 액션 버튼 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                flexWrap: 'wrap',
                marginBottom: 14
              }}>
                <div className="pnav__no" style={{ fontSize: 14 }}>
                  PROJECT NO.{selectedProject.projectNumber}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setShowProjectModal(true)}
                    className="mb-btn mb-btn-secondary mb-btn-sm"
                  >
                    상세보기
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowApplicationsModal(true)}
                    className="mb-btn mb-btn-secondary mb-btn-sm"
                  >
                    계약 상세
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowResultModal(true)}
                    className="mb-btn mb-btn-secondary mb-btn-sm"
                  >
                    결과물
                  </button>
                  {selectedProject.hasCPS && (
                    <button
                      type="button"
                      onClick={() => setShowCPSModal(true)}
                      className="mb-btn mb-btn-secondary mb-btn-sm"
                      style={{ boxShadow: 'inset 0 0 0 1.5px #9a6b00', color: '#9a6b00' }}
                    >
                      CPS 결산
                    </button>
                  )}
                </div>
              </div>

              {/* 프로젝트 성과 요약 — A안 임팩트 카드 */}
              <div className="mbh-impact">
                <p>
                  <b>{selectedProject.performance?.contractedInfluencers || 0}명</b>의 인플루언서가{' '}
                  <b>{selectedProject.performance?.totalChannels || 0}개</b> 계정에서 홍보해<br />
                  약 <b>{(selectedProject.performance?.totalFollowers || 0).toLocaleString()}명</b>에게 노출됐어요
                </p>
                {selectedProject.hasCPS && (
                  <p>구매 고객 <b>{(selectedProject.performance?.totalCustomers || 0).toLocaleString()}명</b>이 유입됐어요</p>
                )}
                <div className="t-note">노출 수는 팔로워 합계 기반 추정치예요.</div>
              </div>

              {/* 핵심 지표 카드 */}
              <div className="metric">
                <div className="metric__card">
                  <span>계약 인플루언서</span>
                  <b>{selectedProject.performance?.contractedInfluencers || 0}<small>명</small></b>
                </div>
                <div className="metric__card">
                  <span>홍보한 SNS 계정</span>
                  <b>{selectedProject.performance?.totalChannels || 0}<small>개</small></b>
                </div>
                <div className="metric__card">
                  <span>노출 팔로워 합계</span>
                  <b>{formatFollowers(selectedProject.performance?.totalFollowers || 0)}</b>
                </div>
                {selectedProject.hasCPS && (
                  <div className="metric__card is-cps">
                    <span>CPS 구매 고객</span>
                    <b>{(selectedProject.performance?.totalCustomers || 0).toLocaleString()}<small>명</small></b>
                  </div>
                )}
              </div>

              {/* 세부 정보 그리드 */}
              <div className="mb-grid-2" style={{ gap: 16 }}>
                {/* 프로젝트 정보 */}
                <div className="mb-card" style={{ margin: 0 }}>
                  <h3 style={{ fontSize: 15.5, fontWeight: 800, color: '#0b1324', margin: '0 0 8px' }}>프로젝트 정보</h3>
                  <dl style={{ margin: 0 }}>
                    <div className="kv">
                      <dt>서비스 방식</dt>
                      <dd>
                        {selectedProject.serviceType === 'delivery' ? '제품 제공형' :
                         selectedProject.serviceType === 'purchase' ? '제품 미제공형' :
                         selectedProject.serviceType === 'visit' ? '장소 방문형' :
                         selectedProject.serviceType === 'free' ? '자율형' : '-'}
                      </dd>
                    </div>
                    <div className="kv">
                      <dt>모집 SNS</dt>
                      <dd>{selectedProject.recruitInfo?.snsTypes?.join(', ') || '-'}</dd>
                    </div>
                    <div className="kv">
                      <dt>콘텐츠 형식</dt>
                      <dd>{selectedProject.recruitInfo?.contentFormats?.join(', ') || '-'}</dd>
                    </div>
                  </dl>
                </div>

                {/* 계약 및 리워드 정보 */}
                <div className="mb-card" style={{ margin: 0 }}>
                  <h3 style={{ fontSize: 15.5, fontWeight: 800, color: '#0b1324', margin: '0 0 8px' }}>계약 · 리워드</h3>
                  <dl style={{ margin: 0 }}>
                    <div className="kv">
                      <dt>계약 / 모집 인원</dt>
                      <dd>{selectedProject.contractedCount || 0}명 / {selectedProject.recruitInfo?.headcount || 0}명</dd>
                    </div>
                    <div className="kv">
                      <dt>1인당 보상</dt>
                      <dd>{(selectedProject.recruitInfo?.rewardPointPerCreator || 0).toLocaleString()}P</dd>
                    </div>
                    <div className="kv">
                      <dt>구매당 리워드</dt>
                      <dd>{selectedProject.hasCPS ? '설정됨' : '미설정'}</dd>
                    </div>
                  </dl>
                </div>

                {/* 일정 정보 */}
                <div className="mb-card" style={{ margin: 0 }}>
                  <h3 style={{ fontSize: 15.5, fontWeight: 800, color: '#0b1324', margin: '0 0 8px' }}>일정</h3>
                  <dl style={{ margin: 0 }}>
                    <div className="kv">
                      <dt>생성일</dt>
                      <dd>{formatDate(selectedProject.scheduleInfo?.createdAt)}</dd>
                    </div>
                    <div className="kv">
                      <dt>모집 마감</dt>
                      <dd>{formatDate(selectedProject.scheduleInfo?.recruitEnd)}</dd>
                    </div>
                    <div className="kv">
                      <dt>제출 마감</dt>
                      <dd>{formatDate(selectedProject.scheduleInfo?.submitEnd)}</dd>
                    </div>
                    <div className="kv">
                      <dt>완료 처리일</dt>
                      <dd>{formatDate(selectedProject.refundInfo?.completedAt)}</dd>
                    </div>
                  </dl>
                </div>

                {/* 금액 정보 */}
                <div className="mb-card" style={{ margin: 0 }}>
                  <h3 style={{ fontSize: 15.5, fontWeight: 800, color: '#0b1324', margin: '0 0 8px' }}>금액</h3>
                  <dl style={{ margin: 0 }}>
                    <div className="kv">
                      <dt>프로젝트 계약금</dt>
                      <dd>{(selectedProject.paymentDetails?.deposit || 0).toLocaleString()}원</dd>
                    </div>
                    <div className="kv">
                      <dt>실제 소진 비용</dt>
                      <dd>{(selectedProject.refundInfo?.totalUsed || 0).toLocaleString()}원</dd>
                    </div>
                    <div className="kv">
                      <dt>환불금</dt>
                      <dd style={{ color: '#2f6bff' }}>{(selectedProject.refundInfo?.refundAmount || 0).toLocaleString()}원</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 모달들 */}
      {showProjectModal && (
        <ProjectDetailModal
          open={showProjectModal}
          onClose={() => setShowProjectModal(false)}
          project={selectedProject?.fullProjectData}
        />
      )}

      {showApplicationsModal && (
        <MarketerApplicationsViewModal
          open={showApplicationsModal}
          onClose={() => setShowApplicationsModal(false)}
          projectId={selectedProject?.id}
          projectNumber={selectedProject?.projectNumber}
        />
      )}

      {showResultModal && (
        <MarketerResultStatusModal
          open={showResultModal}
          onClose={() => setShowResultModal(false)}
          projectId={selectedProject?.id}
          projectNumber={selectedProject?.projectNumber}
        />
      )}

      {showCPSModal && (
        <MarketerCPSSettlementModal
          open={showCPSModal}
          onClose={() => setShowCPSModal(false)}
          projectId={selectedProject?.id}
          projectNumber={selectedProject?.projectNumber}
        />
      )}
    </div>
  );
}

export default MarketerProjectFinishCheck;
